/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import '../../styles/CometChatLogin/CometChatLogin.css';
import { CometChatUIKit } from '@cometchat/chat-uikit-react';

//const API_BASE = 'http://localhost/cometchat-backend';
const API_BASE = 'https://neitclem-connect.gratusinc.org/cometchat-backend';


const CometChatLogin = () => {
  // Authentication states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [loginStatus, setLoginStatus] = useState(''); // For showing login progress
  
  // Multi-step registration states
  const [registerStep, setRegisterStep] = useState(1);
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    avatar: ''
  });

  // Login function
  async function loginWithBackend() {
    if (!email || !password) {
      setMessage('Email and password are required');
      return;
    }

    setIsLoading(true);
    setMessage('');
    setLoginStatus('Authenticating...');

    try {
      const response = await fetch(`${API_BASE}/login.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password
        })
      });

      const data = await response.json();

      if (response.ok) {
        setLoginStatus('Connecting to chat...');
        const userUid = data.user.uid;
        await loginToCometChat(userUid);
      } else {
        setMessage(data.error || 'Login failed');
        setIsLoading(false);
        setLoginStatus('');
      }
    } catch (error) {
      setMessage('Network error: ' + (error as any)?.message);
      setIsLoading(false);
      setLoginStatus('');
    }
  }

  // CometChat login
  async function loginToCometChat(userUid: string) {
    try {
      setLoginStatus('Authenticating user account...');
      const loggedInUser = await CometChatUIKit.login(userUid);
      
      setLoginStatus('Welcome! Setting up your workspace...');
      
      // Small delay to show the final message before redirect
      setTimeout(() => {
        setMessage('Welcome back! Login successful!');
        setIsLoading(false);
        setLoginStatus('');
      }, 1000);
      
    } catch (error) {
      setMessage('Login failed: ' + (error as any)?.message);
      setIsLoading(false);
      setLoginStatus('');
    }
  }

  // Registration function
  async function register() {
    setIsLoading(true);
    setMessage('');
    setLoginStatus('Creating your account...');

    try {
      const response = await fetch(`${API_BASE}/register.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: registerData.name,
          email: registerData.email,
          password: registerData.password,
          avatar: registerData.avatar || undefined
        })
      });

      const data = await response.json();

      if (response.ok) {
        setLoginStatus('Account created successfully!');
        
        setTimeout(() => {
          setMessage('Account created successfully! You can now login.');
          setShowRegister(false);
          setRegisterStep(1);
          setRegisterData({
            name: '',
            email: '',
            password: '',
            confirmPassword: '',
            avatar: ''
          });
          setEmail(data.user.email);
          setIsLoading(false);
          setLoginStatus('');
        }, 1500);
      } else {
        setMessage(data.error || 'Registration failed');
        setIsLoading(false);
        setLoginStatus('');
      }
    } catch (error) {
      setMessage('Network error: ' + (error as any)?.message);
      setIsLoading(false);
      setLoginStatus('');
    }
  }

  // Handle login form submit
  async function handleLoginFormSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    await loginWithBackend();
  }

  // Handle registration step navigation
  function nextStep() {
    if (registerStep === 1) {
      if (!registerData.name || !registerData.email) {
        setMessage('Name and email are required');
        return;
      }
    } else if (registerStep === 2) {
      if (!registerData.password || !registerData.confirmPassword) {
        setMessage('Password and confirmation are required');
        return;
      }
      if (registerData.password !== registerData.confirmPassword) {
        setMessage('Passwords do not match');
        return;
      }
      if (registerData.password.length < 6) {
        setMessage('Password must be at least 6 characters');
        return;
      }
    }
    
    setMessage('');
    if (registerStep < 3) {
      setRegisterStep(registerStep + 1);
    } else {
      register();
    }
  }

  function prevStep() {
    if (registerStep > 1) {
      setRegisterStep(registerStep - 1);
      setMessage('');
    }
  }

  function resetForm() {
    setShowRegister(false);
    setRegisterStep(1);
    setMessage('');
    setEmail('');
    setPassword('');
    setRegisterData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      avatar: ''
    });
  }

  // Render registration steps
  function renderRegistrationStep() {
    switch (registerStep) {
      case 1:
        return (
          <div className="discord-form__step">
            <div className="discord-form__step-header">
              <h2 className="discord-form__step-title">Tell us about yourself</h2>
              <p className="discord-form__step-subtitle">Step 1 of 3</p>
            </div>
            
            <div className="discord-form__input-group">
              <label className="discord-form__label">DISPLAY NAME *</label>
              <input
                className="discord-form__input"
                type="text"
                value={registerData.name}
                onChange={(e) => setRegisterData({...registerData, name: e.target.value})}
                placeholder="Enter your display name"
                disabled={isLoading}
              />
            </div>

            <div className="discord-form__input-group">
              <label className="discord-form__label">EMAIL *</label>
              <input
                className="discord-form__input"
                type="email"
                value={registerData.email}
                onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                placeholder="Enter your email"
                disabled={isLoading}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="discord-form__step">
            <div className="discord-form__step-header">
              <h2 className="discord-form__step-title">Create your password</h2>
              <p className="discord-form__step-subtitle">Step 2 of 3</p>
            </div>
            
            <div className="discord-form__input-group">
              <label className="discord-form__label">PASSWORD *</label>
              <input
                className="discord-form__input"
                type="password"
                value={registerData.password}
                onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                placeholder="Enter your password"
                disabled={isLoading}
              />
            </div>

            <div className="discord-form__input-group">
              <label className="discord-form__label">CONFIRM PASSWORD *</label>
              <input
                className="discord-form__input"
                type="password"
                value={registerData.confirmPassword}
                onChange={(e) => setRegisterData({...registerData, confirmPassword: e.target.value})}
                placeholder="Confirm your password"
                disabled={isLoading}
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="discord-form__step">
            <div className="discord-form__step-header">
              <h2 className="discord-form__step-title">Almost done!</h2>
              <p className="discord-form__step-subtitle">Step 3 of 3 - Optional</p>
            </div>
            
            <div className="discord-form__input-group">
              <label className="discord-form__label">AVATAR URL (OPTIONAL)</label>
              <input
                className="discord-form__input"
                type="url"
                value={registerData.avatar}
                onChange={(e) => setRegisterData({...registerData, avatar: e.target.value})}
                placeholder="https://example.com/your-avatar.jpg"
                disabled={isLoading}
              />
              <p className="discord-form__help-text">
                Leave empty to generate an automatic avatar
              </p>
            </div>

            {registerData.avatar && (
              <div className="discord-form__avatar-preview">
                <img 
                  src={registerData.avatar} 
                  alt="Avatar preview" 
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  }

  return (
    <div className="discord-login__container">
      {/* Login/Register Processing Overlay */}
      {isLoading && (
        <div className="login-overlay">
          <div className="login-overlay__content">
            <div className="login-spinner"></div>
            <div className="login-text">{loginStatus}</div>
          </div>
        </div>
      )}

      <div className="discord-login__background"></div>
      
      <div className="discord-login__content">
        <div className="discord-login__form-container">
          {!showRegister ? (
            // Login Form
            <div className="discord-form">
              <div className="discord-form__header">
                <h1 className="discord-form__title">NeitClem Connect</h1>
                <p className="discord-form__subtitle">Connect Chat Collaborate</p>
              </div>

              {message && (
                <div className={`discord-form__message ${message.includes('successful') ? 'success' : 'error'}`}>
                  {message}
                </div>
              )}

              <form onSubmit={handleLoginFormSubmit} className="discord-form__body">
                <div className="discord-form__input-group">
                  <label className="discord-form__label">EMAIL OR PHONE NUMBER *</label>
                  <input
                    className="discord-form__input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="discord-form__input-group">
                  <label className="discord-form__label">PASSWORD *</label>
                  <input
                    className="discord-form__input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <button 
                  type="submit" 
                  className="discord-form__submit-button"
                  disabled={isLoading}
                >
                  {isLoading ? 'Logging in...' : 'Log In'}
                </button>
              </form>

              <div className="discord-form__footer">
                <span className="discord-form__footer-text">
                  Need an account? 
                </span>
                <button 
                  onClick={() => setShowRegister(true)}
                  className="discord-form__link-button"
                  disabled={isLoading}
                >
                  Register
                </button>
              </div>
            </div>
          ) : (
            // Registration Form
            <div className="discord-form">
              <div className="discord-form__header">
                <h1 className="discord-form__title">Create an account</h1>
                <div className="discord-form__progress">
                  <div className="discord-form__progress-bar">
                    <div 
                      className="discord-form__progress-fill" 
                      style={{ width: `${(registerStep / 3) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              {message && (
                <div className={`discord-form__message ${message.includes('successful') ? 'success' : 'error'}`}>
                  {message}
                </div>
              )}

              <div className="discord-form__body">
                {renderRegistrationStep()}

                <div className="discord-form__step-buttons">
                  {registerStep > 1 && (
                    <button 
                      onClick={prevStep}
                      className="discord-form__secondary-button"
                      disabled={isLoading}
                    >
                      Back
                    </button>
                  )}
                  
                  <button 
                    onClick={nextStep}
                    className="discord-form__submit-button"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : registerStep === 3 ? 'Create Account' : 'Continue'}
                  </button>
                </div>
              </div>

              <div className="discord-form__footer">
                <span className="discord-form__footer-text">
                  Already have an account? 
                </span>
                <button 
                  onClick={resetForm}
                  className="discord-form__link-button"
                  disabled={isLoading}
                >
                  Login
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CometChatLogin;